import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import {
  CsvTypes,
  FileUploadTypes,
  NotificationTypes,
} from '@app/shared/enums';
import { AppStoreState, GrantActions, NotificationActions } from '@app/store';
import { GrantFileUploadResults } from '@core/models';
import { Store } from '@ngrx/store';
import * as Papa from 'papaparse';

@Component({
  selector: 'app-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
  standalone: false,
})
export class FileUploadComponent {
  private store$ = inject<Store<AppStoreState.State>>(Store);

  @Input() fileStatus: string;
  @Input() fileType: FileUploadTypes;

  @Output() addFile = new EventEmitter<GrantFileUploadResults | null>();

  // Note: parseErrors is currently unused. With the abort-on-error approach,
  // parsing is terminated immediately when errors occur, so errors are not
  // accumulated for display. Consider removing this property and the associated
  // template display if detailed error reporting is not needed in the future.
  public parseErrors: Papa.ParseError[] = [];
  public validFile: File | null = null;

  /**
   * Token to track the current file upload attempt.
   * Prevents race conditions when users select multiple files in quick succession.
   * Each new file selection generates a new token, and async operations check
   * that their token matches before proceeding.
   */
  private currentUploadToken = 0;

  /**
   * Resets component state and dispatches error notification and upload status.
   * Centralizes error handling to avoid code duplication.
   *
   * @param errorMessage The error message to display to the user
   */
  private handleUploadError(errorMessage: string): void {
    this.validFile = null;
    this.addFile.emit(null);
    this.store$.dispatch(
      GrantActions.setFileUploadStatus({
        status: { type: this.fileStatus, finished: false },
      }),
    );
    this.store$.dispatch(
      NotificationActions.add({
        notificationType: NotificationTypes.DANGER,
        notificationText: errorMessage,
      }),
    );
  }

  /**
   * Converts bytes to kilobytes with 2 decimal precision.
   *
   * @param bytes The number of bytes to convert
   * @returns The converted value in kilobytes
   */
  public byteConverter(bytes: number): number {
    const k = 1024;
    const convertedBytes = +(bytes / k).toFixed(2);
    return convertedBytes;
  }

  /**
   * Prevents default drag behavior to enable file drop functionality.
   *
   * @param event The drag event
   */
  public drag(event: DragEvent): void {
    event.stopPropagation();
    event.preventDefault();
  }

  /**
   * Handles file drop events and initiates file processing.
   *
   * @param event The drop event containing the dropped file
   */
  public drop(event: DragEvent): void {
    event.preventDefault();
    const file = event.dataTransfer?.files[0];
    if (file) {
      this.selectDataFile(file);
    }
  }

  /**
   * Handles file selection and validates the file before parsing.
   * Performs UTF-8 encoding validation and CSV parsing with streaming support.
   *
   * @param csvFile The CSV file selected by the user (or null/undefined if cancelled)
   */
  public async selectDataFile(csvFile: File | null | undefined): Promise<void> {
    // Guard against null/undefined file selection (e.g., user clicks cancel)
    if (!csvFile) {
      return;
    }

    // Increment token to track this upload attempt and invalidate any previous ones
    // This prevents race conditions if user selects a new file before validation completes
    const uploadToken = ++this.currentUploadToken;

    // First, validate that the file type is a recognized CSV type
    if (!Object.values(CsvTypes).includes(csvFile.type as CsvTypes)) {
      this.handleUploadError(
        'The file you chose is not a CSV file. Please choose a different file.',
      );
      return;
    }

    // Validate UTF-8 encoding before proceeding with parsing
    // This prevents issues with non-UTF-8 files that could cause parsing errors
    const isValidUtf8 = await this.validateUtf8Encoding(csvFile, uploadToken);

    // If token changed during validation, another file was selected - abort this upload
    if (uploadToken !== this.currentUploadToken) {
      return;
    }

    // If encoding validation failed, error already handled in validateUtf8Encoding
    if (!isValidUtf8) {
      return;
    }

    // Proceed with CSV parsing now that we know the file is valid UTF-8
    const parsedFile: Papa.ParseResult<unknown> = {
      data: [],
      errors: [],
      meta: {
        delimiter: '',
        linebreak: '',
        aborted: false,
        truncated: false,
        cursor: 0,
      },
    };

    let isFirstChunk = true;
    let abortParsing = false;

    Papa.parse(csvFile, {
      skipEmptyLines: true,
      chunk: (results, parser: Papa.Parser) => {
        // Check if another file was selected during parsing
        if (uploadToken !== this.currentUploadToken) {
          abortParsing = true;
          parser.abort();
          return;
        }

        if (abortParsing) {
          parser.abort();
          return;
        }

        if (isFirstChunk) {
          // Validate that the CSV uses comma delimiter (not tab, semicolon, etc.)
          if (results.meta.delimiter !== ',') {
            const fileDelimiterErrorMessage =
              'The file delimiter is not a comma. Please ensure the file is a valid CSV.';
            console.error(fileDelimiterErrorMessage);
            this.handleUploadError(fileDelimiterErrorMessage);
            abortParsing = true;
            parser.abort();
            return;
          }
        }

        // If parse errors are detected, abort parsing immediately
        // This prevents the complete callback from emitting a "successful" result
        if (results.errors.length) {
          this.handleUploadError(
            'An error occurred parsing the file. Please check the file format and try uploading again.',
          );
          abortParsing = true;
          parser.abort();
          return;
        }

        // Accumulate parsed data from this chunk
        for (const row of results.data) {
          parsedFile.data.push(row);
        }
        parsedFile.meta = { ...parsedFile.meta, ...results.meta };

        isFirstChunk = false;
      },
      complete: () => {
        // Only emit successful result if parsing wasn't aborted and token still matches
        if (!abortParsing && uploadToken === this.currentUploadToken) {
          this.addFile.emit({
            results: parsedFile,
            type: this.fileType,
            file: csvFile,
          });
          this.validFile = csvFile;
          const noError = { type: this.fileStatus, finished: true };
          this.store$.dispatch(
            GrantActions.setFileUploadStatus({ status: noError }),
          );
        }
      },
      error: (error) => {
        // Handle parsing errors by resetting state and notifying user
        console.error(error);
        this.handleUploadError(
          'An error occurred reading the file. Please try uploading again.',
        );
      },
      header: true,
    });
  }

  /**
   * Validates that a file contains only valid UTF-8 encoded text.
   *
   * Uses streaming validation to:
   * 1. Properly handle multi-byte UTF-8 sequences split across chunk boundaries
   * 2. Detect encoding issues throughout the entire file before CSV parsing
   * 3. Process file in chunks to validate efficiently
   *
   * @param file The file to validate
   * @param uploadToken The token for this upload attempt (to detect if user selected a new file)
   * @returns Promise<boolean> True if file is valid UTF-8, false otherwise
   */
  private async validateUtf8Encoding(
    file: File,
    uploadToken: number,
  ): Promise<boolean> {
    // Chunk size for streaming validation (64KB chunks)
    // Small enough to stream efficiently, large enough to be performant
    const chunkSize = 64 * 1024;
    // Create a single TextDecoder instance for streaming validation
    // Using { fatal: true } makes it throw on invalid UTF-8 sequences
    const decoder = new TextDecoder('utf-8', { fatal: true });
    try {
      let offset = 0;
      while (offset < file.size) {
        // Check if another file was selected during validation
        if (uploadToken !== this.currentUploadToken) {
          return false;
        }
        const end = Math.min(offset + chunkSize, file.size);
        const chunkBuffer = await file.slice(offset, end).arrayBuffer();
        const chunk = new Uint8Array(chunkBuffer);
        // Use { stream: true } for all chunks except the last one
        // This tells TextDecoder to buffer incomplete multi-byte sequences
        // and combine them with the next chunk
        const isLastChunk = end >= file.size;
        decoder.decode(chunk, { stream: !isLastChunk });
        offset = end;
      }
      // If we made it here without exceptions, the file is valid UTF-8
      return true;
    } catch {
      this.handleUploadError(
        "Selected file contains unreadable characters. Please re-save file as 'CSV UTF-8' format and re-try the upload process.",
      );
      return false;
    }
  }
}
